"use client";

import Image from "next/image";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Fragment, useEffect, useState, useRef } from "react";

import type { BlockProps, StrapiBlock } from "@/app/components/blocks/types";

const AUTOPLAY_DELAY = 5000;

type Media = {
    id?: number;
    documentId?: string;
    url?: string | null;
    alternativeText?: string | null;
};

type Plat = {
    id?: number;
    documentId?: string;
    label?: string | null;
    icon?: Media | null;
    image?: Media | null;

    separateurApres?: "plus" | "ou" | "aucun" | null;
};

type Menu = {
    id?: number;
    documentId?: string;
    nom?: string | null;
    description?: string | null;

    prixFiscales?: string | number | null;
    prixTTC?: string | number | null;
    prixHT?: string | number | null;

    plats?: Plat[];
};

type Gamme = {
    id?: number;
    documentId?: string;
    nom?: string | null;
    icon?: Media | null;
    Menu?: Menu | Menu[] | null;
    menu?: Menu | Menu[] | null;
    menus?: Menu[];
};

type SaveurBlock = StrapiBlock & {
    __component: "blocks.saveur";
    titre?: string | null;
    description?: string | null;
    gamme?: Gamme[];
};

type GammeSectionProps = {
    gamme: Gamme;
    index: number;
};

function getMenus(gamme: Gamme): Menu[] {
    const source = gamme.menus ?? gamme.Menu ?? gamme.menu;
    if (Array.isArray(source)) return source;
    return source ? [source] : [];
}

function formatPrice(value?: string | number | null): string {
    if (value === null || value === undefined || value === "") return "";
    return typeof value === "number" ? `${value.toFixed(2)} € TTC` : value;
}



function HighlightedTitle({ title }: { title: string }) {
    const word = "saveurs";
    const position = title.toLowerCase().indexOf(word);

    if (position === -1) return <>{title}</>;

    return (
        <>
            {title.slice(0, position)}
            <span className="text-secondary">{title.slice(position, position + word.length)}</span>
            {title.slice(position + word.length)}
        </>
    );
}

function PlusSeparator() {
    return (
        <div className="relative flex h-14 w-14 shrink-0 items-center justify-center lg:h-9 lg:w-9 xl:h-14 xl:w-14">
            <span
                className="absolute h-7 w-16 -rotate-6 bg-greenPastel lg:h-5 lg:w-10 xl:h-7 xl:w-16"
                style={{ borderRadius: "58% 42% 52% 48% / 50% 45% 55% 50%" }}
            />

            <span className="absolute h-10 w-10 rounded-full border-[3px] border-primaryGreen lg:h-7 lg:w-7 xl:h-10 xl:w-10" />

            <Plus
                size={24}
                strokeWidth={4}
                className="relative z-10 text-primaryGreen lg:h-4 lg:w-4 xl:h-6 xl:w-6"
            />
        </div>
    );
}

function OuSeparator() {
    return (
        <div className="relative flex h-14 w-14 shrink-0 items-center justify-center lg:h-9 lg:w-9 xl:h-14 xl:w-14">
            <span
                className="absolute h-7 w-16 -rotate-6 bg-greenPastel lg:h-5 lg:w-10 xl:h-7 xl:w-16"
                style={{
                    borderRadius:
                        "58% 42% 52% 48% / 50% 45% 55% 50%",
                }}
            />

            <span className="absolute h-10 w-10 rounded-full border-[3px] border-primaryGreen lg:h-7 lg:w-7 xl:h-10 xl:w-10" />

            <span className="relative z-10 text-sm font-bold uppercase text-primaryGreen lg:text-[10px] xl:text-sm">
                OU
            </span>
        </div>
    );
}
function MenuSeparator({
    value,
}: {
    value?: "plus" | "ou" | "aucun" | null;
}) {
    if (value === "plus") {
        return <PlusSeparator />;
    }

    if (value === "ou") {
        return <OuSeparator />;
    }

    // aucun, null 
    return null;
}

function PlatCard({ plat }: { plat: Plat }) {
    const imageUrl = plat.image?.url || "";
    const imageAlt = plat.image?.alternativeText || plat.label || "Élément du menu";
    const iconUrl = plat.icon?.url || "";
    const iconAlt = plat.icon?.alternativeText || plat.label || "";

    return (
        <article className="relative flex w-[165px] shrink-0 flex-col items-center pt-[72px] lg:w-[112px] lg:pt-[56px] xl:w-[165px] xl:pt-[72px]">
            <div className="absolute top-0 flex h-[115px] w-[94px] flex-col items-center rounded-t-xl bg-primaryGreen px-2 pt-4 text-white lg:h-[88px] lg:w-[70px] lg:pt-3 xl:h-[115px] xl:w-[94px] xl:pt-4">

                {iconUrl && (
                    <div className="relative mb-2 h-8 w-8 lg:h-6 lg:w-6 xl:h-8 xl:w-8">
                        <Image
                            src={iconUrl}
                            alt={iconAlt}
                            fill
                            className="object-contain"
                        />
                    </div>
                )}

                {plat.label && (
                    <p className="text-center text-sm font-bold uppercase lg:text-[11px] xl:text-sm">
                        {plat.label}
                    </p>
                )}
            </div>

            <div className="relative z-10 h-[155px] w-[155px] lg:h-[108px] lg:w-[108px] xl:h-[155px] xl:w-[155px]">
                {imageUrl && (
                    <Image
                        src={imageUrl}
                        alt={imageAlt}
                        fill
                        className="object-contain drop-shadow-[0_11px_9px_rgba(0,0,0,0.24)]"
                    />
                )}
            </div>
        </article>
    );
}

type SliderControlsProps = {
    menus: Menu[];
    activeIndex: number;
    onPrevious: () => void;
    onNext: () => void;
    onSelect: (index: number) => void;
    reversed: boolean;
};

function SliderControls({
    menus,
    activeIndex,
    onPrevious,
    onNext,
    onSelect,
    reversed,
}: SliderControlsProps) {
    if (menus.length <= 1) return null;

    return (
        <div className={`mt-7 flex items-center gap-2 ${reversed ? "lg:justify-end" : ""}`}>
            <button
                type="button"
                onClick={onPrevious}
                aria-label="Afficher le menu précédent"
                className="flex h-8 w-8 items-center justify-center rounded-full text-primaryGreen transition hover:bg-greenPastel"
            >
                <ChevronLeft size={21} strokeWidth={2.5} />
            </button>

            <div className="flex items-center gap-1.5">
                {menus.map((menu, index) => (
                    <button
                        key={menu.documentId ?? menu.id ?? index}
                        type="button"
                        onClick={() => onSelect(index)}
                        aria-label={`Afficher le menu ${index + 1}`}
                        aria-current={index === activeIndex ? "true" : undefined}
                        className={`h-3 w-3 rounded-full border transition ${index === activeIndex
                            ? "border-primaryGreen bg-primaryGreen"
                            : "border-greenPastel bg-greenPastel hover:border-primaryGreen"
                            }`}
                    />
                ))}
            </div>

            <button
                type="button"
                onClick={onNext}
                aria-label="Afficher le menu suivant"
                className="flex h-8 w-8 items-center justify-center rounded-full text-primaryGreen transition hover:bg-greenPastel"
            >
                <ChevronRight size={21} strokeWidth={2.5} />
            </button>
        </div>
    );
}

function hasValidPrice(value?: string | number | null): boolean {
    if (value === null || value === undefined || value === "") {
        return false;
    }

    const numericValue = typeof value === "number" ? value : Number(String(value).replace(",", ".").replace(/[^\d.-]/g, ""));

    return (
        Number.isFinite(numericValue) && numericValue > 0
    );
}
function GammeSection({ gamme, index }: GammeSectionProps) {
    const menus = getMenus(gamme);
    const [activeMenuIndex, setActiveMenuIndex] = useState(0);

    const [isPaused, setIsPaused] = useState(false);

    const platsScrollRef = useRef<HTMLDivElement | null>(null);

    const activeMenu = menus[activeMenuIndex] ?? menus[0];

    const hasPrixTTC = hasValidPrice(activeMenu?.prixTTC);

    const hasPrixHT = hasValidPrice(activeMenu?.prixHT);

    const hasPrixFiscales = hasValidPrice(activeMenu?.prixFiscales);

    const isReversed = index % 2 !== 0;
    const isGourmet = gamme.nom?.toLowerCase().includes("gourmet") ?? false;



    useEffect(() => {
        if (activeMenuIndex >= menus.length) setActiveMenuIndex(0);
    }, [activeMenuIndex, menus.length]);

    useEffect(() => {
        if (menus.length <= 1 || isPaused) return;

        const timer = window.setTimeout(() => {
            setActiveMenuIndex((current) => (current + 1) % menus.length);
        }, AUTOPLAY_DELAY);

        return () => window.clearTimeout(timer);
    }, [activeMenuIndex, isPaused, menus.length]);

    useEffect(() => {
        platsScrollRef.current?.scrollTo({
            left: 0,
            behavior: "smooth",
        });
    }, [activeMenuIndex]);

    if (!activeMenu) return null;

    const plats = Array.isArray(activeMenu.plats) ? activeMenu.plats : [];
    const iconUrl = gamme.icon?.url || "";
    const iconAlt = gamme.icon?.alternativeText || gamme.nom || "Gamme";
    const accentColor = isGourmet ? "#C5A04C" : undefined;

    const showPrevious = () => {
        setActiveMenuIndex((current) => (current === 0 ? menus.length - 1 : current - 1));
    };

    const showNext = () => {
        setActiveMenuIndex((current) => (current + 1) % menus.length);
    };

    return (
        <div
            className={`px-6 py-4 lg:px-12 lg:py-8 ${isReversed ? "bg-[#F6F6F6]" : "bg-white"}`}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            <div
                className={`mx-auto grid max-w-[1350px] items-center gap-2 xl:gap-5 ${isReversed
                    ? "xl:grid-cols-[minmax(0,1fr)_310px]"
                    : "xl:grid-cols-[310px_minmax(0,1fr)]"
                    }`}
            >
                <div
                    className={
                        isReversed
                            ? "lg:order-2 lg:flex lg:flex-col lg:items-end lg:text-right"
                            : "lg:order-1"
                    }
                >
                    <div className={`mb-5 flex items-center gap-3 ${isReversed ? "lg:justify-end" : ""}`}>

                        {iconUrl && (
                            <div className="relative h-12 w-12">
                                <Image src={iconUrl} alt={iconAlt} fill className="object-contain" sizes="48px" />
                            </div>
                        )}


                        {gamme.nom && (
                            <p
                                className={`font-bold uppercase ${isGourmet ? "" : "text-secondary"}`}
                                style={isGourmet ? { color: accentColor } : undefined}
                            >
                                {gamme.nom}
                            </p>
                        )}
                    </div>

                    {activeMenu.nom && (
                        <h3 className="font-playfair text-3xl font-bold leading-tight text-[#343434] lg:text-4xl">
                            {activeMenu.nom}
                        </h3>
                    )}

                    {activeMenu.description && (
                        <p className={`mt-5 max-w-[310px] text-lg leading-relaxed text-textColor ${isReversed ? "lg:ml-auto" : ""}`}>
                            {activeMenu.description}
                        </p>
                    )}

                    {hasPrixTTC && hasPrixHT && hasPrixFiscales ? (
                    <div className={`mt-6 ${isReversed ? "lg:flex lg:flex-col lg:items-end" : ""}`}>

                        <span className="inline-block rounded-full bg-primaryGreen px-5 py-2 font-playfair font-semibold text-white">
                            À partir de
                        </span>
                        {hasPrixTTC && (
                            <p className="mt-3 font-playfair text-4xl font-bold text-primaryGreen">
                                {formatPrice(activeMenu.prixTTC)} € TTC
                            </p>
                        )}

                        {
                            hasPrixHT && (
                                <p className="mt-2 text-lg font-normal text-gray-500">
                                    ({formatPrice(activeMenu.prixHT)} € HT)
                                </p>
                            )
                        }

                        {hasPrixFiscales && (
                            <p className="mt-1 text-xl font-semibold text-gray-500">
                                {formatPrice(activeMenu.prixFiscales)} € TTC après aides fiscales *
                            </p>
                        )}



                    </div>
                    ) : null}

                    <SliderControls
                        menus={menus}
                        activeIndex={activeMenuIndex}
                        onPrevious={showPrevious}
                        onNext={showNext}
                        onSelect={setActiveMenuIndex}
                        reversed={isReversed}
                    />
                </div>

                <div
                    ref={platsScrollRef}
                    className={`overflow-x-auto pb-5  ${isReversed ? "lg:order-1" : "lg:order-2"}`}
                >    <div className={`flex min-w-max items-center gap-0 ${isReversed ? "lg:justify-end" : ""}`}>
                        {plats.map((plat, platIndex) => (
                            <Fragment key={plat.documentId ?? plat.id ?? platIndex}>
                                <PlatCard plat={plat} />
                                <MenuSeparator
                                    value={plat.separateurApres}
                                />
                            </Fragment>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}



export default function Saveur({ data }: BlockProps<SaveurBlock>) {
    const gammes = Array.isArray(data.gamme) ? data.gamme : [];

    if (gammes.length === 0) return null;

    return (
        <section className="bg-white">
            <div className="lms-container py-14 text-center lg:py-20">
                {data.titre && (
                    <h2 className="font-playfair text-4xl lg:text-[44px] font-bold text-[#343434] ">
                        <HighlightedTitle title={data.titre} />
                    </h2>
                )}

                {data.description && (
                    <p className="mx-auto mt-5 max-w-[850px] whitespace-pre-line text-lg leading-relaxed text-textColor">
                        {data.description}
                    </p>
                )}
            </div>

            {gammes.map((gamme, index) => (
                <GammeSection
                    key={gamme.documentId ?? gamme.id ?? index}
                    gamme={gamme}
                    index={index}
                />
            ))}
        </section>
    );
}